import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, User, Pill, Building2, ClipboardCheck, Search, Loader2, Save, Lock, Plus, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STEPS = [
  { id: 0, label: "Patient", icon: User },
  { id: 1, label: "Medications", icon: Pill },
  { id: 2, label: "Pharmacy", icon: Building2 },
  { id: 3, label: "Review", icon: ClipboardCheck },
];
const FORMS = ["Tablet", "Capsule", "Syrup", "Suspension", "Cream", "Ointment", "Drops", "Inhaler", "Injection", "Other"];
const ROUTES = ["Oral", "Topical", "Inhaled", "Ophthalmic", "Otic", "Nasal", "Other"];

const blankMed = () => ({ name: "", strength: "", dosage_form: "", route: "", quantity: "", frequency: "", duration: "", instructions: "", refills: "0" });

function calcAge(dob) {
  if (!dob) return "";
  const d = new Date(dob); if (isNaN(d)) return "";
  const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 86400000));
  return age >= 0 ? String(age) : "";
}

export default function CreatePrescription() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { subscription } = useAuth();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [pharmacies, setPharmacies] = useState([]);
  const [phSearch, setPhSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const [patient, setPatient] = useState({ first_name: "", last_name: "", dob: "", age: "", gender: "", patient_ref: "", phone: "", email: "", address: "" });
  const [medications, setMedications] = useState([blankMed()]);
  const [allergies, setAllergies] = useState("");
  const [notes, setNotes] = useState("");
  const [pharmacyId, setPharmacyId] = useState(null);
  const [patientChooses, setPatientChooses] = useState(false);

  const locked = subscription && !subscription.can_create;

  useEffect(() => {
    api.get("/pharmacies").then((r) => setPharmacies(r.data));
    const tid = params.get("template");
    if (tid) {
      api.get("/prescription-templates").then((r) => {
        const t = r.data.find((x) => x.id === tid);
        if (t) { setMedications(t.medications?.length ? t.medications.map((m) => ({ ...blankMed(), ...m })) : [blankMed()]); setNotes(t.notes || ""); toast.success(`Template "${t.name}" applied`); }
      });
    }
    if (id) {
      api.get(`/prescriptions/${id}`).then((r) => {
        const d = r.data;
        setPatient(d.patient); setMedications(d.medications?.length ? d.medications : [blankMed()]);
        setAllergies(d.allergies || ""); setNotes(d.notes || ""); setPharmacyId(d.pharmacy_id); setPatientChooses(d.patient_chooses);
      });
    } else {
      const pre = localStorage.getItem("referu_prefill_patient");
      if (pre) {
        try {
          const p = JSON.parse(pre);
          setPatient((prev) => ({ ...prev, first_name: p.first_name || "", last_name: p.last_name || "", dob: p.dob || "", age: p.age || calcAge(p.dob), gender: p.gender || "", phone: p.phone || "", email: p.email || "", address: p.address || "" }));
        } catch (e) {}
        localStorage.removeItem("referu_prefill_patient");
      }
    }
  }, []);

  useEffect(() => { window.scrollTo({ top: 0 }); }, [step]);

  const setP = (k) => (e) => { const v = e.target.value; setPatient((p) => ({ ...p, [k]: v, ...(k === "dob" ? { age: calcAge(v) } : {}) })); };
  const setMed = (i, k, v) => setMedications((prev) => prev.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const addMed = () => {
    setMedications((p) => [...p, blankMed()]);
    requestAnimationFrame(() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" }));
  };
  const removeMed = (i) => setMedications((p) => p.length > 1 ? p.filter((_, idx) => idx !== i) : p);

  const filteredPh = pharmacies.filter((p) => p.name.toLowerCase().includes(phSearch.toLowerCase()));
  const selectedPh = pharmacies.find((p) => p.id === pharmacyId);

  const canNext = () => {
    if (step === 0) return patient.first_name.trim().length > 0;
    if (step === 1) return medications.some((m) => m.name.trim());
    return true;
  };

  const save = async (asDraft) => {
    setSaving(true);
    try {
      const meds = medications.filter((m) => m.name.trim());
      const payload = { patient, medications: meds, allergies, notes, pharmacy_id: patientChooses ? null : pharmacyId, patient_chooses: patientChooses, status: asDraft ? "draft" : "issued" };
      const res = id ? await api.put(`/prescriptions/${id}`, payload) : await api.post("/prescriptions", payload);
      toast.success(asDraft ? "Draft saved" : "Prescription issued");
      navigate(`/prescriptions/${res.data.id}`);
    } catch (err) {
      if (err.response?.status === 402) { toast.error("Subscription expired"); navigate("/billing"); }
      else toast.error(err.response?.data?.detail || "Could not save prescription");
    } finally { setSaving(false); }
  };

  if (locked) {
    return (
      <div className="p-6 md:p-8 max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl border border-amber-200 p-10 text-center mt-10">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4"><Lock className="w-6 h-6 text-amber-600" /></div>
          <h2 className="text-2xl font-semibold text-slate-900 font-head">Subscription required</h2>
          <p className="text-slate-500 mt-2">Renew to create new prescriptions.</p>
          <Button data-testid="rx-locked-upgrade" onClick={() => navigate("/billing")} className="mt-6 rounded-xl h-12 px-6 bg-blue-600 hover:bg-blue-700">Upgrade now</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto pb-48">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 font-head">{id ? "Edit Prescription" : "New Prescription"}</h1>

      <div className="flex items-center justify-between mt-5 mb-6 overflow-x-auto">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <button onClick={() => i < step && setStep(i)} className="flex flex-col items-center gap-1.5 shrink-0">
              <div className={`w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center ${step === i ? "bg-blue-600 text-white" : step > i ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                {step > i ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs font-medium ${step === i ? "text-blue-600" : "text-slate-400"}`}>{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${step > i ? "bg-emerald-500" : "bg-slate-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-8">
        {step === 0 && (
          <div>
            <div className="mb-5 flex items-start gap-3 bg-blue-50/70 border border-blue-100 rounded-xl p-3">
              <User className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-800">Step 1 of 4 — enter the patient's details. Fields marked * are required. You'll add medications next.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-5">
            <div><Label>First Name *</Label><Input data-testid="rx-patient-first" value={patient.first_name} onChange={setP("first_name")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Last Name</Label><Input data-testid="rx-patient-last" value={patient.last_name} onChange={setP("last_name")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Date of Birth</Label><Input data-testid="rx-patient-dob" type="date" value={patient.dob || ""} onChange={setP("dob")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Age</Label><Input data-testid="rx-patient-age" value={patient.age || ""} readOnly className="mt-1.5 h-12 rounded-xl bg-slate-50" placeholder="auto" /></div>
            <div>
              <Label>Gender</Label>
              <Select value={patient.gender} onValueChange={(v) => setPatient((p) => ({ ...p, gender: v }))}>
                <SelectTrigger data-testid="rx-patient-gender" className="mt-1.5 h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Patient Ref (optional)</Label><Input data-testid="rx-patient-ref" value={patient.patient_ref} onChange={setP("patient_ref")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Phone</Label><Input data-testid="rx-patient-phone" value={patient.phone} onChange={setP("phone")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Email</Label><Input data-testid="rx-patient-email" type="email" value={patient.email} onChange={setP("email")} className="mt-1.5 h-12 rounded-xl" placeholder="for emailing the prescription" /></div>
            <div className="col-span-2"><Label>Address</Label><Input data-testid="rx-patient-address" value={patient.address} onChange={setP("address")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div className="col-span-2"><Label>Known Allergies</Label><Input data-testid="rx-allergies" value={allergies} onChange={(e) => setAllergies(e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="e.g. Penicillin" /></div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 md:space-y-5">
            <div className="flex items-start gap-3 bg-blue-50/70 border border-blue-100 rounded-xl p-3">
              <Pill className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-800">Step 2 of 4 — add each medication with its strength, form, dose and duration. Use <b>Add Medication</b> for multiple items. Sig = directions the patient sees.</p>
            </div>
            {medications.map((m, i) => (
              <div key={i} className="border border-slate-200 rounded-xl p-3 md:p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-blue-600">Medication {i + 1}</p>
                  {medications.length > 1 && <button data-testid={`rx-remove-med-${i}`} onClick={() => removeMed(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
                </div>
                <div className="grid grid-cols-2 gap-2.5 md:gap-3">
                  <div className="col-span-2"><Label>Name *</Label><Input data-testid={`rx-med-name-${i}`} value={m.name} onChange={(e) => setMed(i, "name", e.target.value)} className="mt-1 h-11 rounded-xl" /></div>
                  <div><Label>Strength</Label><Input data-testid={`rx-med-strength-${i}`} value={m.strength} onChange={(e) => setMed(i, "strength", e.target.value)} className="mt-1 h-11 rounded-xl" placeholder="e.g. 500 mg" /></div>
                  <div><Label>Quantity</Label><Input data-testid={`rx-med-qty-${i}`} value={m.quantity} onChange={(e) => setMed(i, "quantity", e.target.value)} className="mt-1 h-11 rounded-xl" /></div>
                  <div>
                    <Label>Dosage Form</Label>
                    <Select value={m.dosage_form} onValueChange={(v) => setMed(i, "dosage_form", v)}>
                      <SelectTrigger data-testid={`rx-med-form-${i}`} className="mt-1 h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{FORMS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Route</Label>
                    <Select value={m.route} onValueChange={(v) => setMed(i, "route", v)}>
                      <SelectTrigger data-testid={`rx-med-route-${i}`} className="mt-1 h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{ROUTES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Frequency</Label><Input data-testid={`rx-med-freq-${i}`} value={m.frequency} onChange={(e) => setMed(i, "frequency", e.target.value)} className="mt-1 h-11 rounded-xl" placeholder="e.g. twice daily" /></div>
                  <div><Label>Duration</Label><Input data-testid={`rx-med-dur-${i}`} value={m.duration} onChange={(e) => setMed(i, "duration", e.target.value)} className="mt-1 h-11 rounded-xl" placeholder="e.g. 7 days" /></div>
                  <div><Label>Refills</Label><Input data-testid={`rx-med-refills-${i}`} value={m.refills} onChange={(e) => setMed(i, "refills", e.target.value)} className="mt-1 h-11 rounded-xl" /></div>
                  <div><Label>Instructions (Sig)</Label><Input data-testid={`rx-med-sig-${i}`} value={m.instructions} onChange={(e) => setMed(i, "instructions", e.target.value)} className="mt-1 h-11 rounded-xl" placeholder="e.g. Take with food" /></div>
                </div>
              </div>
            ))}
            <Button data-testid="rx-add-med" variant="outline" onClick={addMed} className="rounded-xl border-dashed border-slate-300 w-full h-12"><Plus className="w-4 h-4 mr-1" /> Add Medication</Button>
            <div><Label>Additional Notes</Label><Textarea data-testid="rx-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 rounded-xl" /></div>
          </div>
        )}

        {step === 2 && (
          <div>
            <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer mb-4 ${patientChooses ? "border-blue-400 bg-blue-50" : "border-slate-200"}`}>
              <input type="checkbox" data-testid="rx-patient-chooses" checked={patientChooses} onChange={(e) => setPatientChooses(e.target.checked)} className="w-4 h-4" />
              <span className="text-sm font-medium text-slate-700">Patient will choose their own pharmacy</span>
            </label>
            {!patientChooses && (
              <>
                <div className="relative mb-4">
                  <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="rx-pharmacy-search" value={phSearch} onChange={(e) => setPhSearch(e.target.value)} placeholder="Search pharmacies…" className="pl-10 h-12 rounded-xl" />
                </div>
                <div className="space-y-2 max-h-[380px] overflow-y-auto referu-scroll">
                  {filteredPh.map((p) => (
                    <button key={p.id} data-testid={`rx-pharmacy-${p.id}`} onClick={() => setPharmacyId(p.id)} className={`w-full text-left p-4 rounded-xl border ${pharmacyId === p.id ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                      <div className="flex items-center justify-between"><p className="font-semibold text-slate-900">{p.name}</p>{pharmacyId === p.id && <Check className="w-5 h-5 text-blue-600" />}</div>
                      <p className="text-sm text-slate-500 mt-0.5">{p.address}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{p.phone} · {p.email}</p>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 md:space-y-5">
            <div className="border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-blue-600 mb-3">Patient</p>
              <div className="text-sm text-slate-800 space-y-1">
                <p className="font-medium">{patient.first_name} {patient.last_name} · {patient.gender} · {patient.age || "—"} yrs</p>
                {allergies && <p className="text-red-600">Allergies: {allergies}</p>}
              </div>
            </div>
            <div className="border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-blue-600 mb-3">Medications ({medications.filter((m) => m.name.trim()).length})</p>
              {medications.filter((m) => m.name.trim()).map((m, i) => (
                <div key={i} className="text-sm text-slate-800 py-1.5 border-b border-slate-50 last:border-0">
                  <span className="font-medium">{m.name} {m.strength}</span>
                  <span className="text-slate-500"> — {m.dosage_form} {m.route}, {m.frequency} {m.duration && `for ${m.duration}`}. Qty {m.quantity || "—"}, refills {m.refills}</span>
                </div>
              ))}
            </div>
            <div className="border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-blue-600 mb-3">Pharmacy</p>
              <p className="text-sm text-slate-800">{patientChooses ? "Patient will choose pharmacy" : (selectedPh?.name || "Not selected")}</p>
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 md:left-64 right-0 z-20 bg-white border-t border-slate-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex items-center justify-between gap-3">
        <Button data-testid="rx-back-btn" variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} className="rounded-xl h-12 px-5"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="flex items-center gap-2">
          <Button data-testid="rx-save-draft" variant="outline" disabled={saving} onClick={() => save(true)} className="rounded-xl h-12 px-5 border-slate-300"><Save className="w-4 h-4 mr-1" /> Draft</Button>
          {step < 3 ? (
            <Button data-testid="rx-next-btn" disabled={!canNext()} onClick={() => setStep(step + 1)} className="rounded-xl h-12 px-6 bg-blue-600 hover:bg-blue-700">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button data-testid="rx-issue-btn" disabled={saving} onClick={() => save(false)} className="rounded-xl h-12 px-6 bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Issue Prescription <Check className="w-4 h-4 ml-1" /></>}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
